import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Users, Calendar, Package, Pause, Play, XCircle,
  Loader2, RefreshCw, Truck, Clock, ChevronRight, MapPin, Phone
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../../components/DashboardLayout';

const STATUS_COLORS = {
  active: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  paused: 'bg-amber-100 text-amber-700 border-amber-200',
  cancelled: 'bg-red-100 text-red-700 border-red-200',
};

const SubscriberManagement = () => {
  const { toast } = useToast();
  const { apiCall } = useAuth();
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('subscribers');
  const [statusFilter, setStatusFilter] = useState('all');
  const [subscribers, setSubscribers] = useState([]);
  const [summary, setSummary] = useState({});
  const [deliveries, setDeliveries] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  const loadSubscribers = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
      const data = await apiCall(`/api/subscriptions/vendor/subscribers${params}`);
      setSubscribers(data.subscribers || []);
      setSummary(data.summary || {});
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [apiCall, statusFilter]);

  const loadDeliverySchedule = useCallback(async () => {
    try {
      const data = await apiCall('/api/subscriptions/vendor/delivery-schedule');
      setDeliveries(data);
    } catch (e) {
      console.error(e);
    }
  }, [apiCall]);

  useEffect(() => { loadSubscribers(); }, [loadSubscribers]);
  useEffect(() => { if (tab === 'deliveries') loadDeliverySchedule(); }, [tab, loadDeliverySchedule]);

  const handleAction = async (subId, action) => {
    setActionLoading(subId);
    try {
      const result = await apiCall(`/api/subscriptions/vendor/${subId}/${action}`, {
        method: 'POST',
        body: JSON.stringify({ reason: action === 'cancel' ? 'Cancelled by vendor' : undefined }),
      });
      toast({ title: 'Success', description: result.message });
      loadSubscribers();
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setActionLoading(null);
    }
  };

  if (loading && subscribers.length === 0) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64 text-gray-500">
          <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading subscribers...
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-5 max-w-5xl" data-testid="subscriber-management">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Subscribers</h1>
            <p className="text-sm text-gray-500 mt-1">Manage your subscription customers and delivery schedule</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => { loadSubscribers(); if (tab === 'deliveries') loadDeliverySchedule(); }}>
            <RefreshCw className="h-4 w-4 mr-1.5" /> Refresh
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: 'Active', value: summary.active || 0, icon: Users, color: 'text-emerald-600 bg-emerald-50' },
            { label: 'Paused', value: summary.paused || 0, icon: Pause, color: 'text-amber-600 bg-amber-50' },
            { label: 'Cancelled', value: summary.cancelled || 0, icon: XCircle, color: 'text-red-600 bg-red-50' },
            { label: 'Total', value: summary.total || 0, icon: Package, color: 'text-blue-600 bg-blue-50' },
          ].map(s => (
            <Card key={s.label}>
              <CardContent className="p-4 flex items-center gap-3">
                <div className={`p-2.5 rounded-lg ${s.color}`}><s.icon className="h-5 w-5" /></div>
                <div>
                  <p className="text-2xl font-bold">{s.value}</p>
                  <p className="text-xs text-gray-500">{s.label}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="subscribers" className="flex items-center gap-2">
              <Users className="h-4 w-4" /> Subscribers
            </TabsTrigger>
            <TabsTrigger value="deliveries" className="flex items-center gap-2">
              <Truck className="h-4 w-4" /> Today's Deliveries
            </TabsTrigger>
          </TabsList>

          {/* ═══ Subscribers List ═══ */}
          <TabsContent value="subscribers" className="space-y-3 mt-4">
            <div className="flex justify-between items-center">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[160px]" data-testid="status-filter">
                  <SelectValue placeholder="Filter status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="paused">Paused</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
              <span className="text-sm text-gray-500">{subscribers.length} subscriber(s)</span>
            </div>

            {subscribers.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="py-16 text-center">
                  <Users className="h-10 w-10 mx-auto mb-3 text-gray-300" />
                  <p className="font-medium text-gray-600">No subscribers yet</p>
                  <p className="text-sm text-gray-400 mt-1">When customers subscribe to your listings, they'll appear here.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-2">
                {subscribers.map(sub => (
                  <Card key={sub.id} data-testid={`subscriber-${sub.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold text-gray-900 truncate">{sub.customer_name || 'Customer'}</h3>
                            <Badge className={`text-[10px] px-2 border ${STATUS_COLORS[sub.status] || 'bg-gray-100'}`}>
                              {sub.status}
                            </Badge>
                          </div>
                          <p className="text-sm text-gray-600 truncate">{sub.listing_title}</p>
                          <div className="flex flex-wrap gap-3 mt-2 text-xs text-gray-500">
                            <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> {sub.billing_cycle} billing</span>
                            <span className="flex items-center gap-1"><Truck className="h-3 w-3" /> {sub.delivery_frequency} delivery</span>
                            <span className="flex items-center gap-1"><Package className="h-3 w-3" /> Qty: {sub.quantity}</span>
                            {sub.customer_phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" /> {sub.customer_phone}</span>}
                          </div>
                          {sub.shipping_address?.address && (
                            <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                              <MapPin className="h-3 w-3" />
                              {[sub.shipping_address.address, sub.shipping_address.city, sub.shipping_address.pincode].filter(Boolean).join(', ')}
                            </p>
                          )}
                          <div className="flex gap-2 mt-1 text-xs">
                            <span className="text-gray-400">Order: {sub.shopify_order_number}</span>
                            <span className="text-gray-400">Rs.{sub.billing_amount?.toFixed(2)}/{sub.billing_cycle}</span>
                          </div>
                        </div>
                        <div className="flex flex-col gap-1.5 shrink-0">
                          {sub.status === 'active' && (
                            <>
                              <Button size="sm" variant="outline" className="text-amber-600 border-amber-200 hover:bg-amber-50 text-xs"
                                onClick={() => handleAction(sub.id, 'pause')} disabled={actionLoading === sub.id}
                                data-testid={`pause-${sub.id}`}>
                                <Pause className="h-3 w-3 mr-1" /> Pause
                              </Button>
                              <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50 text-xs"
                                onClick={() => { if (window.confirm(`Cancel subscription for ${sub.customer_name}?`)) handleAction(sub.id, 'cancel'); }}
                                disabled={actionLoading === sub.id} data-testid={`cancel-${sub.id}`}>
                                <XCircle className="h-3 w-3 mr-1" /> Cancel
                              </Button>
                            </>
                          )}
                          {sub.status === 'paused' && (
                            <Button size="sm" variant="outline" className="text-emerald-600 border-emerald-200 hover:bg-emerald-50 text-xs"
                              onClick={() => handleAction(sub.id, 'resume')} disabled={actionLoading === sub.id}
                              data-testid={`resume-${sub.id}`}>
                              <Play className="h-3 w-3 mr-1" /> Resume
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* ═══ Delivery Schedule ═══ */}
          <TabsContent value="deliveries" className="space-y-3 mt-4">
            {!deliveries ? (
              <div className="flex items-center justify-center h-32 text-gray-500">
                <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading schedule...
              </div>
            ) : deliveries.total_deliveries === 0 ? (
              <Card className="border-dashed">
                <CardContent className="py-16 text-center">
                  <Truck className="h-10 w-10 mx-auto mb-3 text-gray-300" />
                  <p className="font-medium text-gray-600">No deliveries today</p>
                  <p className="text-sm text-gray-400 mt-1">Active subscriptions with deliveries scheduled for today will show here.</p>
                </CardContent>
              </Card>
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-gray-900">
                      {deliveries.date} ({deliveries.day})
                    </h3>
                    <p className="text-sm text-gray-500">{deliveries.total_deliveries} deliveries across {deliveries.by_listing?.length || 0} listing(s)</p>
                  </div>
                </div>

                {(deliveries.by_listing || []).map(group => (
                  <Card key={group.listing_id}>
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-base">{group.listing_title}</CardTitle>
                        <Badge variant="secondary">{group.total_quantity} units total</Badge>
                      </div>
                      <CardDescription>{group.deliveries.length} delivery(ies)</CardDescription>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <div className="divide-y">
                        {group.deliveries.map((d, i) => (
                          <div key={i} className="py-2.5 flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium">{d.customer_name}</p>
                              <p className="text-xs text-gray-400">
                                {[d.shipping_address?.address, d.shipping_address?.city, d.shipping_address?.pincode].filter(Boolean).join(', ')}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-semibold">Qty: {d.quantity}</p>
                              {d.customer_phone && <p className="text-xs text-gray-400">{d.customer_phone}</p>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default SubscriberManagement;
